import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  EntryPolicySettingRequest,
  EntryPolicySettingState,
  initialEntryPolicySettingState,
} from '../entities/entry-policy-setting.model';
import { EntryPolicySettingService } from '../infra/entry-policy-setting.service';

const OPS = ['load', 'update'] as const;

/**
 * Per-tenant entry/exit policy (SchoolService). A single record: `load` reads
 * the effective policy (the tenant's, or the service defaults) and `update`
 * persists a new policy. This is stored config only — the gate/front enforces
 * it; the service never gates entry/exit itself.
 *
 * Refusals come back as the 007 envelope (422) whose `message` is already
 * localized; the store forwards the raw error to `ErrorStore.setHttpError`,
 * which surfaces `message` verbatim — the front never builds error text.
 */
export const EntryPolicySettingStore = signalStore(
  { providedIn: 'root' },
  withState<EntryPolicySettingState>(initialEntryPolicySettingState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(EntryPolicySettingService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (setting) => patchState(store, { setting }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),

      update: rxMethod<EntryPolicySettingRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (setting) => patchState(store, { setting }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialEntryPolicySettingState, setPropInit(...OPS));
      },
    }),
  ),
);
